(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZugAcrylicCommercialEngine = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var CONTRACT_VERSION = 'zug_acrylic_commercial_contract_v1';
  var CONFIGURED_STRATEGY = 'native_configured_order_variant';
  var DYNAMIC_STRATEGY = 'acrylic_exact_native_rate_units';
  var UNCONSUMED_SUBMISSION_PROPERTIES = [
    '_calc_version', '_legacy_v2_total', '_width_in', '_height_in', '_area_in2',
    '_qty_entered', '_q_low', '_q_high', '_u_interp', '_global_base_scalar',
    '_material_key', '_base_material_mult', '_material_mult', '_rush_option',
    '_rush_mult', '_carrier_rate_cents', '_shape', '_print_side',
    '_print_side_instruction', '_hardware_type', '_hole_type', '_hole_count',
    '_hole_placement', '_production_contract', '_physical_width_in',
    '_physical_height_in', '_target_width_in', '_target_height_in',
    '_bbox_width_px', '_bbox_height_px', '_bbox_x_px', '_bbox_y_px',
    '_dcf_shape_mode', '_dcf_corner_radius_in', '_preview_template',
    '_preview_transform', '_placement_state', '_is_acrylic',
    '_acrylic_subtype_confirmed', '_acrylic_body_path', '_acrylic_outer_path',
    '_acrylic_hole_anchor', '_cutline_svg_path', '_cutline_stroke_color',
    '_cutline_stroke_width', '_original_filename', '_upload_id', '_artwork_mode',
    '_storefront_product_url', 'Material / Thickness', 'Print Side', 'Hole',
    'Hole Count'
  ];
  var SUBTYPES = ['pin', 'keychain', 'charm', 'magnet'];

  function text(value) {
    return String(value == null ? '' : value).trim();
  }

  function integer(value) {
    var parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : NaN;
  }

  function positiveInteger(value) {
    var parsed = integer(value);
    return parsed > 0 ? parsed : 0;
  }

  function exactHundredths(value) {
    var number = Number(value);
    if (!Number.isFinite(number)) return 0;
    var hundredths = Math.round(number * 100);
    return Math.abs(number - hundredths / 100) < 0.0000001 ? hundredths : 0;
  }

  function uniqueNumbers(values) {
    var seen = {};
    return values.map(Number).filter(function (value) {
      if (!Number.isInteger(value) || value <= 0 || seen[value]) return false;
      seen[value] = true;
      return true;
    });
  }

  function assert(condition, message) {
    if (!condition) throw new Error(message);
  }

  function prepare(raw) {
    assert(raw && raw.schemaVersion === 1 && raw.contractVersion === CONTRACT_VERSION,
      'Shared Acrylic commercial contract identity is invalid.');
    assert(raw.family === 'acrylic' && JSON.stringify(raw.subtypes) === JSON.stringify(SUBTYPES),
      'Shared Acrylic subtype contract is invalid.');
    assert(Array.isArray(raw.presetSizes) && raw.presetSizes.length === 5,
      'Shared Acrylic preset-size contract is invalid.');
    assert(Array.isArray(raw.presetQuantities) && raw.presetQuantities.length === 7,
      'Shared Acrylic preset-quantity contract is invalid.');
    assert(Array.isArray(raw.turnarounds) && raw.turnarounds.length === 3,
      'Shared Acrylic turnaround contract is invalid.');
    assert(raw.customQuantity && raw.customQuantity.minimum === 3 &&
      raw.customQuantity.minimumAfterSample === 5 && raw.customQuantity.stepAfterSample === 5 &&
      raw.customQuantity.maximum === 5000 && raw.customQuantity.wholeNumbersOnly === true,
      'Shared Acrylic Custom Quantity contract is invalid.');
    assert(raw.customSize && raw.customSize.minimumWidthIn === 1 &&
      raw.customSize.maximumWidthIn === 4 && raw.customSize.minimumHeightIn === 1 &&
      raw.customSize.maximumHeightIn === 4 && raw.customSize.precisionIn === 0.01 &&
      raw.customSize.maximumAreaIn2 === 9 && raw.customSize.maximumAreaIn2BySubtype &&
      raw.customSize.maximumAreaIn2BySubtype.pin === 9 &&
      raw.customSize.maximumAreaIn2BySubtype.keychain === 9 &&
      raw.customSize.maximumAreaIn2BySubtype.charm === 9 &&
      raw.customSize.maximumAreaIn2BySubtype.magnet === 16,
      'Shared Acrylic Custom Size contract is invalid.');

    var pricing = raw.pricing;
    assert(pricing && pricing.quantityInterpolation === 'linear_unit_price_between_quantity_anchors' &&
      pricing.minimumTotalCents === 1300 && pricing.globalMultiplier === 0.9 &&
      Array.isArray(pricing.quantityBands) &&
      pricing.quantityBands.length === 13 && Array.isArray(pricing.sizeTiersAreaIn2) &&
      pricing.sizeTiersAreaIn2.length === 9 && pricing.baseUnitPriceTable,
      'Shared Acrylic pricing contract is invalid.');
    SUBTYPES.forEach(function (subtype) {
      assert(pricing.subtypeMultipliers[subtype] === 1,
        'Acrylic subtype multiplier is not approved for ' + subtype + '.');
    });
    pricing.sizeTiersAreaIn2.forEach(function (tier) {
      var row = pricing.baseUnitPriceTable[String(tier)];
      assert(row, 'Acrylic size tier is missing pricing.');
      pricing.quantityBands.forEach(function (quantity) {
        assert(Number.isFinite(Number(row[String(quantity)])) && Number(row[String(quantity)]) > 0,
          'Acrylic price anchor is invalid.');
      });
    });

    var rateSchema = raw.nativeRateSchema;
    var denominations = rateSchema && uniqueNumbers(rateSchema.denominationsCents || []);
    assert(rateSchema && rateSchema.strategy === DYNAMIC_STRATEGY && rateSchema.exactOnly === true &&
      positiveInteger(rateSchema.maximumEncodedQuantity) === 999999 &&
      denominations.length === rateSchema.denominationsCents.length && denominations[0] === 1,
      'Shared Acrylic native-rate schema is invalid.');
    for (var index = 1; index < denominations.length; index += 1) {
      assert(denominations[index] > denominations[index - 1],
        'Shared Acrylic denominations must be strictly increasing.');
    }
    return raw;
  }

  function validateQuantity(contract, pieces) {
    var quantity = integer(pieces);
    var rules = contract.customQuantity;
    if (quantity === rules.minimum) return quantity;
    if (quantity < rules.minimumAfterSample || quantity > rules.maximum ||
        quantity % rules.stepAfterSample !== 0) {
      throw new Error('Acrylic quantity must be 3 or a multiple of 5 from 5 through 5,000.');
    }
    return quantity;
  }

  function validateDimensions(contract, widthIn, heightIn, subtype) {
    var widthHundredths = exactHundredths(widthIn);
    var heightHundredths = exactHundredths(heightIn);
    var rules = contract.customSize;
    if (!widthHundredths || !heightHundredths) {
      throw new Error('Acrylic dimensions must use no more than 0.01-inch precision.');
    }
    if (widthHundredths < rules.minimumWidthIn * 100 ||
        widthHundredths > rules.maximumWidthIn * 100 ||
        heightHundredths < rules.minimumHeightIn * 100 ||
        heightHundredths > rules.maximumHeightIn * 100) {
      throw new Error('Acrylic dimensions must each be from 1.00 through 4.00 inches.');
    }
    var exactSubtype = text(subtype);
    assert(SUBTYPES.indexOf(exactSubtype) !== -1, 'Acrylic subtype is unsupported.');
    var maximumAreaIn2 = rules.maximumAreaIn2BySubtype[exactSubtype];
    var areaHundredThousandths = widthHundredths * heightHundredths;
    if (areaHundredThousandths > maximumAreaIn2 * 10000) {
      throw new Error('Acrylic dimensions exceed the ' + Number(maximumAreaIn2).toFixed(2) +
        '-square-inch maximum for ' + exactSubtype + '.');
    }
    return {
      widthIn: widthHundredths / 100,
      heightIn: heightHundredths / 100,
      widthHundredths: widthHundredths,
      heightHundredths: heightHundredths,
      areaIn2: areaHundredThousandths / 10000
    };
  }

  function turnaroundRow(contract, key) {
    var matches = contract.turnarounds.filter(function (row) { return row.key === text(key); });
    if (matches.length !== 1) throw new Error('Acrylic turnaround is unsupported.');
    return matches[0];
  }

  function sizeTier(contract, areaIn2) {
    for (var index = 0; index < contract.pricing.sizeTiersAreaIn2.length; index += 1) {
      var tier = contract.pricing.sizeTiersAreaIn2[index];
      if (tier >= areaIn2) return tier;
    }
    throw new Error('Acrylic area does not have an approved pricing tier.');
  }

  function quantityBounds(contract, pieces) {
    var bands = contract.pricing.quantityBands;
    if (pieces <= bands[0]) return { low: bands[0], high: bands[0] };
    if (pieces >= bands[bands.length - 1]) {
      return { low: bands[bands.length - 1], high: bands[bands.length - 1] };
    }
    for (var index = 0; index < bands.length; index += 1) {
      if (bands[index] >= pieces) {
        if (bands[index] === pieces) return { low: pieces, high: pieces };
        return { low: bands[index - 1], high: bands[index] };
      }
    }
    throw new Error('Acrylic quantity does not have approved pricing anchors.');
  }

  function calculate(rawContract, input) {
    var contract = prepare(rawContract);
    input = input || {};
    var subtype = text(input.subtype);
    assert(SUBTYPES.indexOf(subtype) !== -1, 'Acrylic subtype is unsupported.');
    var dimensions = validateDimensions(contract, input.widthIn, input.heightIn, subtype);
    var pieces = validateQuantity(contract, input.pieces);
    var turnaround = turnaroundRow(contract, input.turnaround);
    var tier = sizeTier(contract, dimensions.areaIn2);
    var bounds = quantityBounds(contract, pieces);
    var prices = contract.pricing.baseUnitPriceTable[String(tier)];
    var lowPrice = Number(prices[String(bounds.low)]);
    var highPrice = Number(prices[String(bounds.high)]);
    var interpolated = lowPrice;
    if (bounds.low !== bounds.high) {
      interpolated = lowPrice + ((pieces - bounds.low) / (bounds.high - bounds.low)) *
        (highPrice - lowPrice);
    }
    var preGlobalUnitPrice = interpolated * contract.pricing.subtypeMultipliers[subtype] *
      turnaround.multiplier;
    var unitPrice = preGlobalUnitPrice * contract.pricing.globalMultiplier;
    var totalCents = Math.round(Math.max(
      contract.pricing.minimumTotalCents / 100,
      unitPrice * pieces
    ) * 100);
    var magnetQuote = null;
    if (subtype === 'magnet' && input.magnetPricingEnabled === true) {
      assert(input.magnetPricing && typeof input.magnetPricing.quote === 'function',
        'Magnet pricing is unavailable.');
      magnetQuote = input.magnetPricing.quote('acrylic', dimensions.widthIn, dimensions.heightIn,
        pieces, turnaround.multiplier, contract.nativeRateSchema.denominationsCents,
        contract.nativeRateSchema.maximumEncodedQuantity);
      totalCents = magnetQuote.totalCents;
      unitPrice = magnetQuote.unitPrice;
      preGlobalUnitPrice = unitPrice;
      tier = magnetQuote.sizeTier;
      bounds = { low: magnetQuote.qLow, high: magnetQuote.qHigh };
    }
    assert(totalCents > 0, 'Acrylic canonical total is invalid.');
    var canonical = {
      subtype: subtype,
      widthIn: dimensions.widthIn,
      heightIn: dimensions.heightIn,
      areaIn2: dimensions.areaIn2,
      sizeTierIn2: tier,
      pieces: pieces,
      turnaround: turnaround.key,
      turnaroundOption: turnaround.optionValue,
      qLow: bounds.low,
      qHigh: bounds.high,
      preGlobalUnitPriceUnrounded: preGlobalUnitPrice,
      unitPriceUnrounded: unitPrice,
      globalMultiplier: magnetQuote ? 1 : contract.pricing.globalMultiplier,
      totalCents: totalCents
    };
    if (magnetQuote) canonical.magnetPricingVersion = magnetQuote.version;
    return canonical;
  }

  function validateSourceIdentity(subtype, adapter, input) {
    assert(adapter && adapter.subtype === subtype && text(adapter.sourceProductId) &&
      text(adapter.sourceHandle), 'Acrylic subtype identity adapter is invalid.');
    assert(text(input.sourceProductId) === text(adapter.sourceProductId) &&
      text(input.sourceHandle) === text(adapter.sourceHandle),
      'Acrylic source product identity does not match the subtype contract.');
    var tags = Array.isArray(input.tags) ? input.tags.map(function (tag) {
      return text(tag).toLowerCase();
    }) : [];
    assert(tags.filter(function (tag) { return tag === 'dcf:type:acrylic'; }).length === 1,
      'Acrylic source product must contain one exact family tag.');
    var subtypeTags = tags.filter(function (tag) { return tag.indexOf('dcf:acrylic_id:') === 0; });
    assert(subtypeTags.length === 1 && subtypeTags[0] === 'dcf:acrylic_id:' + subtype,
      'Acrylic source product must contain one exact subtype tag.');
  }

  function configuredRows(adapter) {
    return adapter && adapter.configuredContract && Array.isArray(adapter.configuredContract.rows)
      ? adapter.configuredContract.rows : [];
  }

  function validateConfiguredAdapter(rawContract, adapter) {
    var contract = prepare(rawContract);
    var configured = adapter && adapter.configuredContract;
    assert(adapter && SUBTYPES.indexOf(adapter.subtype) !== -1 && configured &&
      configured.enabled === true && configured.catalogReady === true &&
      configured.strategy === CONFIGURED_STRATEGY && configured.shopifyQuantity === 1 &&
      text(configured.sourceProductId) === text(adapter.sourceProductId) &&
      text(configured.sourceHandle) === text(adapter.sourceHandle),
      'Acrylic configured-order identity adapter is invalid.');
    var rows = configuredRows(adapter);
    assert(rows.length === 105 && configured.rowCount === 105,
      'Acrylic configured-order adapter must contain 105 snapshot rows.');
    var keys = {};
    var variants = {};
    rows.forEach(function (row) {
      assert(!keys[row.key] && !variants[row.variantId],
        'Acrylic configured-order snapshot contains duplicate identity.');
      keys[row.key] = true;
      variants[row.variantId] = true;
      var canonical = calculate(contract, {
        subtype: adapter.subtype,
        widthIn: row.widthIn,
        heightIn: row.heightIn,
        pieces: row.pieces,
        turnaround: row.turnaround
      });
      assert(canonical.totalCents === positiveInteger(row.priceCents),
        'Acrylic configured-order price snapshot disagrees with the shared commercial contract.');
    });
    return true;
  }

  function presetResolution(contract, adapter, canonical) {
    var matches = configuredRows(adapter).filter(function (row) {
      return Number(row.widthIn) === canonical.widthIn &&
        Number(row.heightIn) === canonical.heightIn &&
        positiveInteger(row.pieces) === canonical.pieces &&
        text(row.turnaround) === canonical.turnaround;
    });
    if (!matches.length) return null;
    assert(matches.length === 1, 'Acrylic preset has ambiguous configured-order identity.');
    var row = matches[0];
    assert(positiveInteger(row.priceCents) === canonical.totalCents,
      'Acrylic configured-order snapshot price is not canonical.');
    return {
      strategy: CONFIGURED_STRATEGY,
      contract: text(adapter.configuredContract.contract),
      productId: positiveInteger(adapter.sourceProductId),
      variantId: positiveInteger(row.variantId),
      encodedQuantity: 1,
      representedTotalCents: canonical.totalCents,
      configurationKey: text(row.key),
      sku: text(row.proposedSku),
      variantTitle: text(row.proposedVariantTitle)
    };
  }

  function validateRateCatalog(contract, adapter) {
    var catalog = adapter && adapter.rateCatalog;
    assert(catalog && catalog.strategy === DYNAMIC_STRATEGY && catalog.exactOnly === true &&
      catalog.subtype === adapter.subtype && text(catalog.sourceProductId) === text(adapter.sourceProductId) &&
      positiveInteger(catalog.carrierProductId), 'Acrylic native-rate subtype catalog is invalid.');
    var expected = contract.nativeRateSchema.denominationsCents;
    var rates = Array.isArray(catalog.rates) ? catalog.rates : [];
    assert(rates.length === expected.length, 'Acrylic native-rate catalog has the wrong denomination count.');
    var seenVariants = {};
    rates.forEach(function (rate, index) {
      assert(positiveInteger(rate.rateCents) === expected[index] && positiveInteger(rate.variantId) &&
        !seenVariants[rate.variantId], 'Acrylic native-rate mapping is invalid.');
      seenVariants[rate.variantId] = true;
    });
    return rates;
  }

  function dynamicResolution(contract, adapter, canonical) {
    var rates = validateRateCatalog(contract, adapter);
    var maximum = contract.nativeRateSchema.maximumEncodedQuantity;
    for (var index = rates.length - 1; index >= 0; index -= 1) {
      var rate = rates[index];
      if (canonical.totalCents % rate.rateCents !== 0) continue;
      var quantity = canonical.totalCents / rate.rateCents;
      if (quantity <= maximum) {
        return {
          strategy: DYNAMIC_STRATEGY,
          contract: CONTRACT_VERSION,
          productId: positiveInteger(adapter.rateCatalog.carrierProductId),
          variantId: positiveInteger(rate.variantId),
          rateCents: rate.rateCents,
          encodedQuantity: quantity,
          representedTotalCents: rate.rateCents * quantity
        };
      }
    }
    throw new Error('Acrylic total cannot be represented exactly below the Shopify quantity ceiling.');
  }

  function resolve(rawContract, input) {
    var contract = prepare(rawContract);
    input = input || {};
    var subtype = text(input.subtype);
    var adapter = input.adapter;
    validateSourceIdentity(subtype, adapter, input);
    validateConfiguredAdapter(contract, adapter);
    var canonical = calculate(contract, input);
    // Configured variants retain their original Shopify prices. Updated magnet
    // quotes use the existing exact native rates for BOTH display and cart.
    var representation = (!canonical.magnetPricingVersion && presetResolution(contract, adapter, canonical)) ||
      dynamicResolution(contract, adapter, canonical);
    assert(representation.representedTotalCents === canonical.totalCents,
      'Acrylic native representation has price variance.');
    return {
      contractVersion: CONTRACT_VERSION,
      sourceProductId: text(adapter.sourceProductId),
      sourceHandle: text(adapter.sourceHandle),
      canonical: canonical,
      representation: representation
    };
  }

  function sizeLabel(canonical) {
    return canonical.widthIn.toFixed(2) + '" x ' + canonical.heightIn.toFixed(2) + '"';
  }

  function assertSubmittedProperty(properties, key, canonicalValue) {
    if (!Object.prototype.hasOwnProperty.call(properties, key)) return;
    assert(text(properties[key]) === text(canonicalValue),
      'Caller-supplied ' + key + ' does not match canonical Acrylic resolution.');
  }

  function buildNativeLine(rawContract, input) {
    input = input || {};
    var resolved = resolve(rawContract, input);
    var canonical = resolved.canonical;
    var uid = text(input.uid);
    assert(uid, 'Acrylic configuration UID is required.');
    var callerProperties = input.callerProperties || {};
    assertSubmittedProperty(callerProperties, 'Finished Size', sizeLabel(canonical));
    assertSubmittedProperty(callerProperties, 'Pieces', String(canonical.pieces));
    assertSubmittedProperty(callerProperties, 'Turnaround', canonical.turnaroundOption);
    if (input.callerTargetTotalCents != null) {
      assert(positiveInteger(input.callerTargetTotalCents) === canonical.totalCents,
        'Caller-supplied Acrylic total does not match canonical pricing.');
    }
    var freePieces = input.freePieces == null ? 0 : integer(input.freePieces);
    // The canonical cart line removes routine source fields below. BOGO uses
    // the originating storefront path to route the cart title back to its
    // promotional product, so retain that exact path only for a BOGO line.
    var bogoStorefrontProductUrl = text((input.artworkProperties || {}).
      _storefront_product_url || (input.trustedSubtypeProperties || {}).
      _storefront_product_url);
    assert(freePieces === 0 || ((canonical.subtype === 'pin' || canonical.subtype === 'charm' ||
      canonical.subtype === 'keychain' || canonical.subtype === 'magnet') && canonical.pieces >= 25 &&
      canonical.pieces <= 1000 && canonical.pieces % 5 === 0 && freePieces === canonical.pieces),
    'Acrylic BOGO requires 25–1,000 paid pieces in multiples of 5, with one free piece per paid piece.');

    var properties = Object.assign({}, input.artworkProperties || {}, input.trustedSubtypeProperties || {}, {
      'Finished Size': sizeLabel(canonical),
      'Pieces': String(canonical.pieces),
      'Turnaround': canonical.turnaroundOption,
      '_calc_line_uid': uid,
      '_pricing_engine': 'true',
      '_line_type': 'base',
      '_product_family': 'acrylic',
      '_acrylic_subtype': canonical.subtype,
      '_carrier_strategy': resolved.representation.strategy,
      '_carrier_contract': resolved.representation.contract,
      '_carrier_source_product_id': resolved.sourceProductId,
      '_carrier_product_id': String(resolved.representation.productId),
      '_carrier_encoded_quantity': String(resolved.representation.encodedQuantity),
      '_native_total_cents': String(canonical.totalCents),
      '_width_in': String(canonical.widthIn),
      '_height_in': String(canonical.heightIn),
      '_area_in2': String(canonical.areaIn2),
      '_size_tier_in2': String(canonical.sizeTierIn2),
      '_qty_entered': String(canonical.pieces),
      '_rush_option': canonical.turnaround,
      '_bundle_quantity_locked': 'true',
      '_bundle_display_quantity': String(canonical.pieces)
    });
    if (resolved.representation.rateCents) {
      properties._carrier_rate_cents = String(resolved.representation.rateCents);
    }
    if (freePieces > 0) {
      var productionQuantity = canonical.pieces + freePieces;
      properties['Paid Pieces'] = String(canonical.pieces);
      properties['Free Pieces'] = String(freePieces);
      properties['Pieces'] = String(productionQuantity);
      properties._paid_quantity = String(canonical.pieces);
      properties._production_quantity = String(productionQuantity);
      // Keep the cart's locked quantity display aligned with the paid amount.
      // The visible Pieces property and production metadata carry the BOGO total.
      properties._bundle_display_quantity = String(canonical.pieces);
      try {
        var calcData = JSON.parse(properties._calc_data || '{}');
        calcData.paidQty = canonical.pieces;
        calcData.freeQty = freePieces;
        calcData.productionQty = productionQuantity;
        properties._calc_data = JSON.stringify(calcData);
      } catch (error) {
        throw new Error('Acrylic BOGO production data is invalid.');
      }
    }
    // These values are consumed before submission or duplicate canonical
    // production data. Guard, read-back, grouping, quantity-lock, title, and
    // artwork/proof properties are deliberately excluded from this cleanup.
    UNCONSUMED_SUBMISSION_PROPERTIES.forEach(function (key) {
      delete properties[key];
    });
    if (freePieces > 0 && bogoStorefrontProductUrl) {
      properties._storefront_product_url = bogoStorefrontProductUrl;
    }
    return {
      items: [{
        id: resolved.representation.variantId,
        quantity: resolved.representation.encodedQuantity,
        properties: properties
      }],
      targetTotalCents: canonical.totalCents,
      resolution: resolved
    };
  }

  function verifyCartReadback(bundle, cart) {
    assert(bundle && bundle.items && bundle.items.length === 1 && cart && Array.isArray(cart.items),
      'Acrylic Cart read-back input is invalid.');
    var expected = bundle.items[0];
    var uid = text(expected.properties && expected.properties._calc_line_uid);
    var grouped = cart.items.filter(function (line) {
      return text(line.properties && line.properties._calc_line_uid) === uid;
    });
    assert(grouped.length === 1, 'Acrylic Cart read-back must contain exactly one native line.');
    var line = grouped[0];
    assert(positiveInteger(line.product_id) ===
      positiveInteger(bundle.resolution.representation.productId),
      'Acrylic Cart read-back product identity mismatch.');
    assert(positiveInteger(line.variant_id || line.id) === positiveInteger(expected.id) &&
      positiveInteger(line.quantity) === positiveInteger(expected.quantity),
      'Acrylic Cart read-back variant or encoded quantity mismatch.');
    var represented = Number.isInteger(line.original_line_price) ? line.original_line_price : line.line_price;
    assert(represented === bundle.targetTotalCents,
      'Acrylic Cart read-back total does not match canonical pricing.');
    if (Number.isInteger(line.final_line_price)) {
      assert(line.final_line_price >= 0 && line.final_line_price <= represented,
        'Acrylic Cart read-back discount total is invalid.');
    }
    ['Finished Size', 'Pieces', 'Turnaround', '_acrylic_subtype', '_carrier_strategy',
      '_carrier_contract', '_carrier_source_product_id', '_carrier_product_id',
      '_carrier_encoded_quantity', '_native_total_cents'].forEach(function (key) {
      assert(text(line.properties && line.properties[key]) === text(expected.properties[key]),
        'Acrylic Cart read-back property mismatch: ' + key + '.');
    });
    if (expected.properties && expected.properties._production_quantity != null) {
      ['Paid Pieces', 'Free Pieces', '_paid_quantity', '_production_quantity',
        '_bundle_display_quantity'].forEach(function (key) {
        assert(text(line.properties && line.properties[key]) === text(expected.properties[key]),
          'Acrylic Cart read-back BOGO property mismatch: ' + key + '.');
      });
      var expectedCalcData;
      var actualCalcData;
      try {
        expectedCalcData = JSON.parse(expected.properties._calc_data || '{}');
        actualCalcData = JSON.parse(line.properties && line.properties._calc_data || '{}');
      } catch (error) {
        throw new Error('Acrylic Cart read-back BOGO calculation data is invalid.');
      }
      ['paidQty', 'freeQty', 'productionQty'].forEach(function (key) {
        assert(actualCalcData[key] === expectedCalcData[key],
          'Acrylic Cart read-back BOGO calculation data mismatch: ' + key + '.');
      });
    }
    assert(!cart.items.some(function (candidate) {
      return text(candidate.properties && candidate.properties._calc_line_uid) === uid &&
        text(candidate.properties && candidate.properties._line_type) === 'adjustment';
    }), 'Acrylic Cart read-back contains forbidden adjustment merchandise.');
    return true;
  }

  return {
    CONTRACT_VERSION: CONTRACT_VERSION,
    CONFIGURED_STRATEGY: CONFIGURED_STRATEGY,
    DYNAMIC_STRATEGY: DYNAMIC_STRATEGY,
    SUBTYPES: SUBTYPES.slice(),
    prepare: prepare,
    validateQuantity: validateQuantity,
    validateDimensions: validateDimensions,
    calculate: calculate,
    validateConfiguredAdapter: validateConfiguredAdapter,
    validateRateCatalog: validateRateCatalog,
    resolve: resolve,
    buildNativeLine: buildNativeLine,
    verifyCartReadback: verifyCartReadback
  };
});
